import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrg } from '../../components/OrgLayout'
import { supabase } from '../../lib/supabase'
import {
  VStack,
  HStack,
  Card,
  PageHeader,
  Text,
  Button,
  Badge,
  Table,
  Banner,
  Animate,
  Avatar
} from 'wss3-forge'
import {
  Add20Regular,
  Checkmark20Regular,
  Dismiss20Regular,
  Apps20Regular
} from '@fluentui/react-icons'

interface SsoConnectionWithApp {
  id: string
  app_id: string | null
  app_name: string
  app_image_url: string | null
  protocol: string
  is_active: boolean
  jit_provisioning: boolean
  created_at: string
}

export function OrgSso() {
  const navigate = useNavigate()
  const { organization } = useOrg()

  const [connections, setConnections] = useState<SsoConnectionWithApp[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadConnections()
  }, [organization.id])

  const loadConnections = async () => {
    try {
      const { data, error } = await supabase.rpc('get_org_sso_connections', {
        p_organization_id: organization.id
      })

      if (error) throw error
      setConnections(data || [])
    } catch (err: any) {
      console.error('Error loading SSO connections:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <VStack gap="lg">
      <Animate type="fadeIn">
        <PageHeader
          title="SSO Connections"
          subtitle="Configure SSO (SAML or OIDC) for Webba applications."
        />
      </Animate>

      {error && (
        <Banner variant="error" title="Error" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      <Animate type="slideInUp" delay={50}>
        <Table
          title="SSO Connections"
          columns={[
            {
              key: 'app',
              header: 'Application',
              render: (_, row: SsoConnectionWithApp) => (
                <HStack gap="sm" style={{ alignItems: 'center' }}>
                  {row.app_id ? (
                    <Avatar
                      src={row.app_image_url || undefined}
                      name={row.app_name}
                      size="sm"
                    />
                  ) : (
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'var(--brand-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Apps20Regular style={{ color: 'white', fontSize: 16 }} />
                    </div>
                  )}
                  <Text weight="medium">{row.app_name}</Text>
                </HStack>
              )
            },
            {
              key: 'protocol',
              header: 'Protocol',
              render: (_, row: SsoConnectionWithApp) => (
                <Badge>{row.protocol.toUpperCase()}</Badge>
              )
            },
            {
              key: 'jit',
              header: 'JIT',
              render: (_, row: SsoConnectionWithApp) => (
                row.jit_provisioning
                  ? <Checkmark20Regular style={{ color: '#10B981' }} />
                  : <Dismiss20Regular style={{ color: 'var(--text-muted)' }} />
              )
            },
            {
              key: 'status',
              header: 'Status',
              render: (_, row: SsoConnectionWithApp) => (
                <Badge variant={row.is_active ? 'success' : 'default'}>
                  {row.is_active ? 'Active' : 'Inactive'}
                </Badge>
              )
            },
            {
              key: 'actions',
              header: '',
              render: (_, row: SsoConnectionWithApp) => (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate(`/org/${organization.slug}/sso/${row.id}`)}
                >
                  Configure
                </Button>
              )
            }
          ]}
          data={connections}
          loading={loading}
          onRowClick={(row) => navigate(`/org/${organization.slug}/sso/${row.id}`)}
          globalActions={
            <Button
              size="sm"
              icon={<Add20Regular />}
              onClick={() => navigate(`/org/${organization.slug}/sso/new`)}
            >
              Add Connection
            </Button>
          }
        />
      </Animate>

      <Animate type="slideInUp" delay={100}>
        <Card variant="outlined" padding="lg" title="Supported Protocols">
          <HStack gap="lg">
            <VStack gap="xs" style={{ alignItems: 'flex-start' }}>
              <Badge size="lg">SAML 2.0</Badge>
              <Text size="sm" color="muted">
                Compatible with Azure AD, Okta, OneLogin, Google Workspace
              </Text>
            </VStack>
            <VStack gap="xs" style={{ alignItems: 'flex-start' }}>
              <Badge size="lg">OpenID Connect</Badge>
              <Text size="sm" color="muted">
                Compatible with Azure AD, Auth0, Keycloak, and more
              </Text>
            </VStack>
          </HStack>
        </Card>
      </Animate>
    </VStack>
  )
}
